// src/pages/Rental.js
import React, { useEffect, useMemo, useState } from "react";
import Reveal from "../components/Reveal";
import BookingCalendar from "../components/BookingCalendar";
import BookingRequestForm from "../components/BookingRequestForm";
import "../styles/Rental.css";
import heroBg from "../assets/rentalbanner.jpg";
import banquetImg from "../assets/banquethall.jpg";
import boardroomImg from "../assets/boardroom.jpg";

const SPACES = {
  BANQUET_HALL: {
    key: "BANQUET_HALL",
    label: "Banquet Hall",
    description:
      "A spacious, multi-purpose room ideal for weddings, socials, anniversaries, conferences, fundraisers, and special occasions. Capacity up to 400 guests.",
    image: banquetImg,
    rates: [
      { label: "Daytime (6AM - 6PM)", value: "$50 per hour" },
      { label: "Evening (6PM - 1AM)", value: "$100 per hour" },
    ],
    fees: [
      { label: "Damage Deposit", value: "$300 (refundable)" },
      { label: "Set-up/Cleaning (6AM - 6PM)", value: "$150" },
      { label: "Set-up/Cleaning (6PM - 1AM)", value: "$200" },
      { label: "Bartenders (2)", value: "$136 for 4 hours" },
      { label: "Security Guards (2)", value: "$160 for 4 hours" },
    ],
    notes: [
      "All rates subject to 5% GST.",
      "Minimum rental is three (3) hours plus one (1) hour free for prep.",
      "Two-hour gap between events is observed.",
      "SOCAN fee applies when recorded music is played.",
      "Rental Agreement and Damage Deposit required to guarantee booking.",
    ],
  },
  BOARDROOM: {
    key: "BOARDROOM",
    label: "Boardroom",
    description:
      "Private meeting space for small groups, presentations, or workshops. Includes microwave, kettle, coffee maker, and coat rack for convenience.",
    image: boardroomImg,
    rates: [{ label: "Daily", value: "$25 per hour" }],
    fees: [],
    notes: [
      "Comfortable layout for planning sessions and presentations.",
      "Bring your own laptop/HDMI if you need a projector or screen.",
    ],
  },
};

const defaultRange = () => {
  const start = new Date();
  start.setMinutes(0, 0, 0);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return { start: start.toISOString(), end: end.toISOString() };
};

function Rental() {
  const [activeSpace, setActiveSpace] = useState("BANQUET_HALL");
  const [bookings, setBookings] = useState([]);
  const [range, setRange] = useState({ start: null, end: null });
  const [loading, setLoading] = useState(false);
  const [selectedRange, setSelectedRange] = useState(defaultRange());
  const [refreshKey, setRefreshKey] = useState(0);
  const [error, setError] = useState("");

  const space = useMemo(() => SPACES[activeSpace], [activeSpace]);

  useEffect(() => {
    if (!range.start || !range.end) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          space: activeSpace,
          start: range.start,
          end: range.end,
        });
        const res = await fetch(`/api/bookings?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load bookings");
        if (!cancelled) setBookings(data.bookings || []);
      } catch (err) {
        if (!cancelled) setError(err.message || "Unable to load availability");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [range, activeSpace, refreshKey]);

  const handleSelect = (info) => {
    setSelectedRange({ start: info.startStr, end: info.endStr });
    window.scrollTo({ top: document.body.scrollHeight / 2, behavior: "smooth" });
  };

  const handleRequestClick = () => {
    const now = new Date();
    const start = new Date(now.getTime() + 60 * 60 * 1000);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setSelectedRange({ start: start.toISOString(), end: end.toISOString() });
    window.scrollTo({ top: document.body.scrollHeight / 2, behavior: "smooth" });
  };

  const refreshBookings = () => setRefreshKey((k) => k + 1);

  return (
    <div className="rental-page">
      <div className="page-header" style={{ backgroundImage: `url(${heroBg})` }}>
        <div className="overlay"></div>
        <div className="container header-content">
          <Reveal>
            <h1>Community Spaces</h1>
            <p>
              Host your gatherings in welcoming, flexible rooms designed for celebrations,
              meetings, and community events.
            </p>
          </Reveal>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <Reveal>
            <div className="rental-selector">
              {Object.values(SPACES).map((s) => (
                <button
                  key={s.key}
                  className={`tab-btn ${activeSpace === s.key ? "active" : ""}`}
                  onClick={() => setActiveSpace(s.key)}
                >
                  <h3>{s.label}</h3>
                </button>
              ))}
            </div>
          </Reveal>

          <div className="space-details card">
            <div className="space-card-image-wrapper">
              <img src={space.image} alt={space.label} className="card-img" />
            </div>
            <div className="card-content">
              <h3>{space.label}</h3>
              <p>{space.description}</p>
              <div className="space-flex">
                <div>
                  <h4>Rates</h4>
                  <ul className="amenities-list">
                    {space.rates.map((r, idx) => (
                      <li key={idx}>
                        <span className="left">{r.label}:</span>{" "}
                        <span className="right">{r.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4>Fees & Services</h4>
                  <ul className="amenities-list">
                    {space.fees.length ? (
                      space.fees.map((f, idx) => (
                        <li key={idx}>
                          <span className="left">{f.label}:</span>{" "}
                          <span className="right">{f.value}</span>
                        </li>
                      ))
                    ) : (
                      <li>Contact us for details.</li>
                    )}
                  </ul>
                </div>
              </div>
              {space.notes?.length ? (
                <div className="notes-rental">
                  <h4>Additional Information</h4>
                  <ul>
                    {space.notes.map((note, idx) => (
                      <li key={idx}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <a href="/contact" className="btn btn-secondary btn-sm">
                Contact the PCCM Team
              </a>
            </div>
          </div>

          <div className="booking-layout">
            <BookingCalendar
              bookings={bookings}
              loading={loading}
              onRangeChange={(r) => setRange(r)}
              onSelectSlot={handleSelect}
              onRequestClick={handleRequestClick}
            />
            <BookingRequestForm
              initialSpace={activeSpace}
              initialStart={selectedRange?.start}
              initialEnd={selectedRange?.end}
              onSubmitted={refreshBookings}
            />
          </div>
          {error ? <p className="text-error" style={{ marginTop: 12 }}>{error}</p> : null}
        </div>
      </section>
    </div>
  );
}

export default Rental;
